import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CandlestickChartIcon from "@mui/icons-material/CandlestickChart";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import { Card, CardContent, Skeleton, Typography } from "@mui/material";
import type { GetSystemReportQueryResult } from "Api/Api";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import {
  StyledFlex,
  StyledPageWrapper,
} from "Components/Shared/StyledComponents";
import { CurrencyCode } from "Models/CurrencyCodes";
import type { RootStateType } from "State/Store";
import { getSystemReportAsync } from "State/System/SystemReport/SystemReportState";
import { formatCurrency } from "Utils/CurrencyUtils";
import { getSystemVersion } from "Utils/VersioningUtils";
import BigNumber from "bignumber.js";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { Resources, useResource } from "Translations/Resources";

const Grid = styled.div`
  margin-top: ${(props) => props.theme.spacing(3)};
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 20px;
  row-gap: 20px;
`;

const getBalanceControlSum = (reportResult: GetSystemReportQueryResult) => {
  const balanceControlSum = new BigNumber(reportResult.walletBalance.balance)
    .plus(new BigNumber(reportResult.coldWalletBalance.balance))
    .plus(new BigNumber(reportResult.exchangeWithdrawSum))
    .plus(new BigNumber(reportResult.sellTransactionSum))
    .minus(new BigNumber(reportResult.userAccountBalance))
    .minus(new BigNumber(reportResult.withdrawalSum));

  console.log(
    "controlSum:",
    balanceControlSum.toNumber(),
    "report.walletBalance.balance:",
    reportResult.walletBalance.balance,
    "report.coldWalletBalance.balance:",
    reportResult.coldWalletBalance.balance,
    "btcExchangeBalance:",
    reportResult.exchangeWithdrawSum,
    "report.userAccountBalance:",
    reportResult.userAccountBalance,
    "report.sellTransactionSum:",
    reportResult.sellTransactionSum,
  );
  return balanceControlSum;
};

export const SystemReportPage = () => {
  const dispatch = useDispatch();
  const { t } = useResource();
  React.useEffect(() => {
    dispatch(getSystemReportAsync.request());

    return () => {
      dispatch(getSystemReportAsync.success(null));
    };
  }, [dispatch]);

  const { report } = useSelector((e: RootStateType) => e.system);

  return (
    <AuthenticatedLayout title="Systémový report">
      <StyledPageWrapper>
        <div className="left-content">
          <Grid>
            <StyledFlex gap="10px" alignItems="center">
              <AccountBalanceWalletIcon />
              <Typography variant="h3">Mo.one</Typography>
            </StyledFlex>
            <div />

            <div>
              <Card>
                <CardContent>
                  <Typography>+ Na Hot Wallet</Typography>
                  {!report ? (
                    <Skeleton width={120} />
                  ) : (
                    <Typography>
                      {formatCurrency({
                        amount: report.walletBalance.balance,
                        currencyCode: CurrencyCode.BTC,
                      })}
                    </Typography>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography>+ Na Cold Wallet</Typography>
                  {!report ? (
                    <Skeleton width={120} />
                  ) : (
                    <Typography>
                      {formatCurrency({
                        amount: report.coldWalletBalance.balance,
                        currencyCode: CurrencyCode.BTC,
                      })}
                    </Typography>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography> + K výběru z burzy </Typography>
                  {!report ? (
                    <Skeleton width={120} />
                  ) : (
                    <Typography>
                      {formatCurrency({
                        amount: report.exchangeWithdrawSum,
                        currencyCode: CurrencyCode.BTC,
                      })}
                    </Typography>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography>- Na účtech uživatelů</Typography>
                  {!report ? (
                    <Skeleton width={120} />
                  ) : (
                    <Typography>
                      {formatCurrency({
                        amount: report.userAccountBalance,
                        currencyCode: CurrencyCode.BTC,
                      })}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardContent>
                <Typography variant="h3">Kontrolní suma</Typography>
                {!report ? (
                  <Skeleton width={120} />
                ) : (
                  <Typography>
                    {formatCurrency({
                      amount: getBalanceControlSum(report).toNumber(),
                      currencyCode: CurrencyCode.BTC,
                    })}
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h3">
                  K výběru ze systémové peňeženky
                </Typography>
                {!report ? (
                  <Skeleton width={120} />
                ) : (
                  <Typography>
                    {formatCurrency({
                      amount: report.withdrawalSum,
                      currencyCode: CurrencyCode.BTC,
                    })}
                  </Typography>
                )}
              </CardContent>
            </Card>

            <div>
              <Card>
                <CardContent>
                  <Typography variant="h3">
                    {t(Resources.System.BlockchainFee)}
                  </Typography>
                  {!report ? (
                    <Skeleton width={120} />
                  ) : (
                    <Typography>
                      {formatCurrency({
                        amount: report.blockchainFee,
                        currencyCode: CurrencyCode.BTC,
                      })}
                      per vByte
                    </Typography>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="h3">
                    {t(Resources.System.MempoolFeeLevel)}
                  </Typography>
                  {!report ? (
                    <Skeleton width={120} />
                  ) : (
                    <Typography>{report.mempoolFeeLevel}</Typography>
                  )}
                </CardContent>
              </Card>
            </div>

            <StyledFlex gap="10px" alignItems="center">
              <CurrencyExchangeIcon />
              <Typography variant="h3">
                {t(Resources.System.Exchange)}
              </Typography>
            </StyledFlex>
            <div />

            <div>
              <Card>
                <CardContent>
                  <Typography>K výběru z burzy </Typography>
                  {!report ? (
                    <Skeleton width={120} />
                  ) : (
                    <Typography>
                      {formatCurrency({
                        amount: report.exchangeWithdrawSum,
                        currencyCode: CurrencyCode.BTC,
                      })}
                    </Typography>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography>Poplatek za výběr z burzy</Typography>
                  {!report ? (
                    <Skeleton width={120} />
                  ) : (
                    <Typography>
                      {formatCurrency({
                        amount: report.exchangeWithdrawalFee,
                        currencyCode: CurrencyCode.BTC,
                      })}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </div>
            <div>
              {report?.coinmateBalances.map((e) => (
                <Card key={e.currencyCode}>
                  <CardContent>
                    <Typography>{`Zústatek ${e.currencyCode} na burze`}</Typography>

                    {formatCurrency({
                      amount: e.availableBalance,
                      currencyCode: e.currencyCode,
                    })}
                  </CardContent>
                </Card>
              ))}
            </div>
            <StyledFlex gap="10px" alignItems="center">
              <CandlestickChartIcon />
              <Typography variant="h3">{t(Resources.System.Trades)}</Typography>
            </StyledFlex>
            <div />
            <div>
              <Card>
                <CardContent>
                  <Typography>Obchodovaný objem</Typography>
                  {!report ? (
                    <Skeleton width={120} />
                  ) : (
                    <Typography>
                      {formatCurrency({
                        amount: report.coinmateVolume.askedVolume,
                        currencyCode: report.coinmateVolume.currencyCode,
                      })}
                    </Typography>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography>Za měsíc</Typography>
                  {!report ? (
                    <Skeleton width={120} />
                  ) : (
                    <Typography>
                      {formatCurrency({
                        amount: report.coinmateMonthlyVolume.askedVolume,
                        currencyCode: report.coinmateMonthlyVolume.currencyCode,
                      })}
                    </Typography>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography>Za týden</Typography>
                  {!report ? (
                    <Skeleton width={120} />
                  ) : (
                    <Typography>
                      {formatCurrency({
                        amount: report.coinmateWeeklyVolume.askedVolume,
                        currencyCode: report.coinmateWeeklyVolume.currencyCode,
                      })}
                    </Typography>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography>Za 24h</Typography>
                  {!report ? (
                    <Skeleton width={120} />
                  ) : (
                    <Typography>
                      {formatCurrency({
                        amount: report.coinmateDailyVolume.askedVolume,
                        currencyCode: report.coinmateDailyVolume.currencyCode,
                      })}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </div>
            <div>
              <Card>
                <CardContent>
                  <Typography>Nakoupené BTC</Typography>
                  {!report ? (
                    <Skeleton width={120} />
                  ) : (
                    <Typography>
                      {formatCurrency({
                        amount: report.coinmateBoughtBtc,
                        currencyCode: CurrencyCode.BTC,
                      })}
                    </Typography>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography>Vybráno nebo k výběru</Typography>
                  {!report ? (
                    <Skeleton width={120} />
                  ) : (
                    <Typography>
                      {formatCurrency({
                        amount: report.withdrawalTransactionSum,
                        currencyCode: CurrencyCode.BTC,
                      })}
                    </Typography>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography>Suma účetní knihy BTC</Typography>
                  {!report ? (
                    <Skeleton width={120} />
                  ) : (
                    <Typography>
                      {formatCurrency({
                        amount: report.accountingSumBTC,
                        currencyCode: CurrencyCode.BTC,
                      })}
                    </Typography>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography>Suma účetní knihy CZK</Typography>
                  {!report ? (
                    <Skeleton width={120} />
                  ) : (
                    <Typography>
                      {formatCurrency({
                        amount: report.accountingSumCZK,
                        currencyCode: CurrencyCode.CZK,
                      })}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </div>
          </Grid>
          <br />
          Verze systému: {getSystemVersion()}
        </div>
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};

export default SystemReportPage;
